import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { isFirebaseConfigured } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Eye, EyeOff, Check, X, Phone } from "lucide-react";

const LOGO = "/smartfx-logo.png";

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

const PW_RULES = [
  { key: "len", label: "At least 6 characters", test: (p) => p.length >= 6 },
  { key: "upper", label: "One uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
  { key: "lower", label: "One lowercase letter (a-z)", test: (p) => /[a-z]/.test(p) },
  { key: "special", label: "One special character ($ ? @ ! ...)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function Register() {
  const { register, loginWithGoogle, playTransition, endTransition } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  const pwChecks = PW_RULES.map((r) => ({ ...r, ok: r.test(password) }));
  const pwValid = pwChecks.every((c) => c.ok);
  const match = confirm.length > 0 && password === confirm;
  const usernameHasInvalid = username.length > 0 && !/^[a-zA-Z]*$/.test(username);
  const usernameValid = /^[a-zA-Z]{3,20}$/.test(username);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!usernameValid) { setError("Username can only contain letters (no numbers or symbols), 3-20 characters. Example: davidnapolleon"); return; }
    if (!pwValid) { setError("Your password does not meet all the requirements."); return; }
    if (!match) { setError("The two passwords do not match."); return; }
    setBusy(true);
    try {
      await register({ name, email, password, username, phone });
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const googleSubmit = async () => {
    setError("");
    setGoogleBusy(true);
    try {
      await loginWithGoogle();
      await playTransition("login");
      navigate("/app");
      setTimeout(endTransition, 700);
    } catch (err) {
      const code = err?.code || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        setGoogleBusy(false);
        return;
      }
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setGoogleBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grain flex flex-col">
      <div className="p-6">
        <Link to="/" data-testid="back-home-link" className="inline-flex items-center gap-2 text-sm text-[#888] hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <div className="w-full max-w-md fade-up">
          <div className="flex items-center gap-2 mb-8">
            <img src={LOGO} alt="Smart FX" className="h-11 w-auto object-contain" />
            <span className="font-heading text-xl font-bold tracking-tight">SMART FX</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Create your account</h1>
          <p className="text-[#888] text-sm mb-8">Connect your MT5 account and let the trade automatically.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Full name</Label>
              <Input id="name" data-testid="register-name-input" required value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-2 bg-[#0F0F0F] border-[#222] rounded-sm h-11 focus:ring-white focus:border-white" />
            </div>
            <div>
              <Label htmlFor="username" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Username</Label>
              <div className={`mt-2 flex items-center bg-[#0F0F0F] border rounded-sm h-11 transition-colors ${usernameHasInvalid ? "border-[#FF3B30]" : "border-[#222] focus-within:border-white"}`}>
                <span className="pl-3 pr-1 text-[#888] select-none font-medium">@</span>
                <input id="username" data-testid="register-username-input" required value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/^@+/, ""))}
                  placeholder="davidnapolleon"
                  className="flex-1 bg-transparent pr-3 h-full outline-none text-white text-sm placeholder:text-[#555]" />
              </div>
              {usernameHasInvalid ? (
                <p className="text-xs text-[#FF3B30] mt-1.5" data-testid="username-error">Letters only — no numbers or symbols. Example: davidnapolleon</p>
              ) : (
                <p className="text-xs text-[#666] mt-1.5">Letters only, no numbers. Example: davidnapolleon</p>
              )}
            </div>
            <div>
              <Label htmlFor="email" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Email</Label>
              <Input id="email" data-testid="register-email-input" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-[#0F0F0F] border-[#222] rounded-sm h-11 focus:ring-white focus:border-white" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Phone number</Label>
              <div className="mt-2 flex items-center bg-[#0F0F0F] border border-[#222] rounded-sm h-11 focus-within:border-white transition-colors">
                <span className="pl-3 pr-1 text-[#888] select-none"><Phone className="h-4 w-4" /></span>
                <input id="phone" data-testid="register-phone-input" type="tel" required value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 809 000 0000"
                  className="flex-1 bg-transparent pr-3 h-full outline-none text-white text-sm placeholder:text-[#555]" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Password</Label>
              <div className="relative mt-2">
                <Input id="password" data-testid="register-password-input" type={showPass ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0F0F0F] border-[#222] rounded-sm h-11 pr-11 focus:ring-white focus:border-white" />
                <button type="button" data-testid="toggle-password" onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-white transition-colors" aria-label={showPass ? "Hide password" : "Show password"}>
                  {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-3 space-y-1.5" data-testid="password-rules">
                {pwChecks.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 text-xs" data-testid={`pw-rule-${c.key}`}>
                    <span className={`h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${c.ok ? "bg-[#00F593]/20 text-[#00F593]" : "bg-[#222] text-[#666]"}`}>
                      {c.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    </span>
                    <span className={c.ok ? "text-[#cfe0f5]" : "text-[#888]"}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-xs font-mono-term uppercase tracking-widest text-[#888]">Confirm password</Label>
              <div className="relative mt-2">
                <Input id="confirm" data-testid="register-confirm-input" type={showConfirm ? "text" : "password"} required value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="bg-[#0F0F0F] border-[#222] rounded-sm h-11 pr-11 focus:ring-white focus:border-white" />
                <button type="button" data-testid="toggle-confirm" onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888] hover:text-white transition-colors" aria-label={showConfirm ? "Hide password" : "Show password"}>
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirm.length > 0 && !match && <p className="text-xs text-[#FF3B30] mt-1.5" data-testid="confirm-mismatch">Passwords do not match.</p>}
            </div>
            {error && <p data-testid="register-error" className="text-sm text-[#FF3B30]">{error}</p>}
            <Button type="submit" data-testid="register-submit-button" disabled={busy}
              className="w-full h-11 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          {isFirebaseConfigured() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-[#222]" />
                <span className="text-xs text-[#666] uppercase tracking-widest">or</span>
                <div className="h-px flex-1 bg-[#222]" />
              </div>
              <button type="button" data-testid="google-register-button" onClick={googleSubmit} disabled={googleBusy || busy}
                className="w-full h-11 rounded-sm bg-white text-black font-semibold flex items-center justify-center gap-2.5 hover:bg-[#e5e5e5] transition-colors disabled:opacity-60">
                {googleBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><GoogleG /> Continue with Google</>}
              </button>
            </>
          )}

          <p className="text-sm text-[#888] mt-6">
            Already registered?{" "}
            <Link to="/login" data-testid="go-login-link" className="text-white underline underline-offset-4">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
